import type {ImageSource} from '@/types/image'

const EXPONENT = 2.5

// Linear scale (no transformation)
const linearTransform = (value: number) => value

// Cubic scale (slow in, fast out)
const cubicTransform = (value: number, min: number, max: number) => {
    const normalized = (value - min) / (max - min) // Normalize to 0-1
    const transformed = Math.pow(normalized, EXPONENT) // Cubic transformation

    return min + transformed * (max - min) // Map back to min-max range
}

// Inverse cubic scale (for number input to slider position)
const inverseCubicTransform = (value: number, min: number, max: number) => {
    const normalized = (value - min) / (max - min) // Normalize to 0-1
    const transformed = Math.pow(normalized, 1 / EXPONENT) // Inverse cubic

    return min + transformed * (max - min) // Map back to min-max range
}

// Validate and constrain values
export const getValidValue = (value: number, min: number, max: number, step: number) => {
    const roundedValue = Math.round(value / step) * step

    return Math.min(Math.max(roundedValue, min), max)
}

export const transformValue = (
    value: number,
    mode: 'linear' | 'cubic',
    min: number,
    max: number,
) => {
    return mode === 'cubic' ? cubicTransform(value, min, max) : linearTransform(value)
}

export const inverseTransformValue = (
    value: number,
    mode: 'linear' | 'cubic',
    min: number,
    max: number,
) => {
    return mode === 'cubic' ? inverseCubicTransform(value, min, max) : linearTransform(value)
}

export const getIconFromValue = (
    value: number,
    min: number,
    max: number,
    step: number,
    icons?: [ImageSource, ImageSource, ImageSource],
) => {
    if (!icons) {
        return null
    }

    const [smallIcon, mediumIcon, largeIcon] = icons

    const thirdOfRange = (max - min) / 3
    const valueIsBetween = isWithinRange(getValidValue(value, min, max, step))

    // Changes the icon for every third of the slider range
    switch (true) {
        case valueIsBetween(min, thirdOfRange):
            return smallIcon
        case valueIsBetween(thirdOfRange, thirdOfRange * 2):
            return mediumIcon
        case valueIsBetween(thirdOfRange * 2, max):
            return largeIcon
        default: {
            return null
        }
    }
}

const isWithinRange = (value: number) => (start: number, end: number) =>
    value >= start && value <= end
